import React from 'react'
import styled from 'styled-components'

import Accordion from '@jarvis.oddle/core/components/Accordion'
import { getSelectedVariantInfo } from '@jarvis.oddle/core/tools/product'
import { VariantHeader } from '@jarvis.oddle/core/molecules/ProductDetailsModal'
import {
  ContentContainer,
  ItemContainer
} from '@jarvis.oddle/core/molecules/ProductDetailsModal/ContentVariantElements'
import Form from '@jarvis.oddle/core/components/Form'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import {
  marginSizes,
  buttonRadiusValues,
  typo,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'

import { useAppContext } from 'src/containers/AppContext'
import useCustomisation from 'src/hooks/useCustomisation'
import { formatDiff } from 'src/utils/number'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import ProductOption from './ProductOption'
import {
  checkExistNutriGradeTag,
  NutriGradeIcon
} from 'src/components/icons/NutriGrade'
import { getAllProductNutrigradeTags } from 'src/utils/product'

interface ProductVariantProps {
  variants: Menu.Variant[]
  id: string
  selectedVariantId: string
  calculateOptionDiff: (optionId: string, variantId: string) => number
  hideArrow: boolean
  subItemVariantsKey: string
  calculateVariantDiff: (variantId: string) => number
}

const RadioContainer = styled(ItemContainer)`
  align-items: flex-start;

  &:hover {
    cursor: pointer;
  }
`

const NameContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  alignItems: 'center'
}))`
  flex: 0 0 70%;
  ${breakpoint('tablet')`
    flex: 0 0 80%;
  `}
`

const AmountContainer = styled(Flex)`
  flex: 0 0 30%;
  ${breakpoint('tablet')`
    flex: 0 0 20%;
  `}
`

const Amount = styled(H4).attrs(() => ({
  marginTop: marginSizes.small,
  marginBottom: marginSizes.small,
  textAlign: 'right'
}))`
  /* work around to handle edge cases,
  until we have a new ProductOptionModal design */
  word-break: break-all;
`

const WrappedVariantName = styled(Div)`
  position: relative;
`

const StyledRadioDot = styled(Form.Radio.Dot)`
  margin-bottom: auto;
`

const TagContainer = styled(Flex)`
  flex-wrap: wrap;
  ${breakpoint('mobile')`
    flex-wrap: none;
  `}
`

const VariantTagContainer = styled.div`
  display: flex;
  flex-flow: wrap;
  grid-gap: 5px;
`

const VariantTag = styled.div`
  display: inline-block;
  background: white;
  padding: 4px 5px;
  border: 1px solid #bbb;
  color: black;
  font-family: 'Roboto', sans-serif;
  border-radius: ${(props) => buttonRadiusValues[props.theme.buttonShape]};
  font-size: ${typo.smallText.desktopFontSize};
`

const VariantContent = ({
  show = true,
  variants = [],
  formatMoney = (x) => x.toString(),
  calculateVariantDiff = (x) => 0,
  variantPriceDisplay = 'priceDifference'
}: {
  show?: boolean
  variants?: Menu.Variant[]
  formatMoney?: (x: number) => string
  calculateVariantDiff?: (x: string) => number
  variantPriceDisplay?: 'priceDifference'
}) => {
  const getVariantOptionPrice = ({ variant }) => {
    if (variantPriceDisplay === 'priceDifference') {
      const variantDiff = calculateVariantDiff(variant.id)
      return variantDiff <= 0
        ? ''
        : formatDiff({ value: variantDiff, formatter: formatMoney })
    } else {
      return formatMoney(variant.price)
    }
  }

  return (
    <ContentContainer show={show}>
      <Form.RadioGroup
        name='selectedVariant'
        selected={variants.length > 0 && variants[0].id}
      >
        {(variants || []).map((variant, idx) => (
          <Form.Radio
            key={idx}
            value={variant.id}
            Container={(props) => (
              <RadioContainer
                key={variant.id}
                last={idx === variants.length - 1}
                {...props}
              />
            )}
          >
            {({ checked }) => {
              const existNutriGradeTag = checkExistNutriGradeTag(variant.tags)
              const variantNutrigradeTags = getAllProductNutrigradeTags(
                variant.tags
              )
              return (
                <React.Fragment>
                  <NameContainer>
                    <StyledRadioDot checked={checked} />
                    <Div paddingLeft={paddingSizes.medium}>
                      <WrappedVariantName>
                        <H4 margin='none'>{variant.variantName}</H4>
                      </WrappedVariantName>
                      {(variantNutrigradeTags || existNutriGradeTag) && (
                        <TagContainer>
                          <Div paddingRight={paddingSizes.extraSmall}>
                            <NutriGradeIcon
                              tags={variant.tags}
                              height={40}
                              width={89}
                            />
                          </Div>
                          {variantNutrigradeTags && (
                            <Div>
                              <VariantTagContainer>
                                {variantNutrigradeTags.map((tag) => (
                                  <VariantTag key={tag}>{tag}</VariantTag>
                                ))}
                              </VariantTagContainer>
                            </Div>
                          )}
                        </TagContainer>
                      )}
                    </Div>
                  </NameContainer>
                  <AmountContainer>
                    <Amount data-testid={`text.amount.${idx}`}>
                      {getVariantOptionPrice({ variant })}
                    </Amount>
                  </AmountContainer>
                </React.Fragment>
              )
            }}
          </Form.Radio>
        ))}
      </Form.RadioGroup>
    </ContentContainer>
  )
}

const ProductVariant = ({
  variants,
  id,
  selectedVariantId,
  calculateVariantDiff,
  hideArrow,
  calculateOptionDiff,
  subItemVariantsKey
}: ProductVariantProps): JSX.Element => {
  const { getSectionFromId } = useCustomisation()
  const { formatMoney } = useAppContext()
  if (!variants || variants.length <= 1) return null
  const { selectedVariant, selectedVarPriceDiff } = getSelectedVariantInfo(
    variants,
    selectedVariantId
  )
  const { settings = {} } = getSectionFromId('productDetail')
  const { variantPriceDisplay } = settings
  return (
    <React.Fragment>
      <Accordion.Section
        id={id}
        renderHeader={({ show }) => (
          <VariantHeader
            show={show}
            hideArrow={hideArrow}
            formatMoney={formatMoney}
            selectedVariant={selectedVariant}
            selectedVarPriceDiff={selectedVarPriceDiff}
            variantPriceDisplay={variantPriceDisplay}
          />
        )}
        renderContent={({ show }) => (
          <VariantContent
            show={show}
            variants={variants}
            formatMoney={formatMoney}
            calculateVariantDiff={calculateVariantDiff}
            variantPriceDisplay={variantPriceDisplay}
          />
        )}
      />
      {selectedVariant.subitems &&
        selectedVariant.subitems.map((option) => (
          <ProductOption
            subItemVariantsKey={subItemVariantsKey}
            key={option.id}
            option={option}
            hideArrow={option[subItemVariantsKey].length <= 1}
            calculateOptionDiff={calculateOptionDiff}
          />
        ))}
    </React.Fragment>
  )
}

export default ProductVariant
